import { useSuspenseQuery } from '@tanstack/react-query';
import {
	createRootRouteWithContext,
	type ErrorComponentProps,
	HeadContent,
	Outlet,
	Scripts,
	useRouter,
	useRouterState,
} from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { IntlProvider } from 'react-intl';

import '../styles/tailwind.css';

import { NAVBAR_NOTIFICATIONS_QUERY_KEY } from '~/components/main-navbar/notifications/query.ts';
import { getMessages } from '~/i18n/messages.ts';
import type { RouterContext } from '~/router-context.ts';
import { isAuthenticatedAppPath } from '~/routing/authenticated-app-paths.ts';
import { APP_SHELL_QUERY_KEY, getAppShellDataQueryOptions } from '~/server/app/app-shell-data-query.ts';
import { NOTIFICATIONS_PAGE_QUERY_KEY } from '~/server/notifications/notifications-page-data-query.ts';
import { hydrateLocalModeInstrumentCache, ResolveIsHydrated } from '~/store/index.ts';
import { cn } from '~/utils/cn.ts';
import { DEFAULT_LOCALE } from '~/utils/format.ts';

import { MainNavbar } from '../components/main-navbar/main-navbar.tsx';
import { GeneralErrorBoundaryView } from '../components/root/general-error-boundary-view.tsx';
import { StartInstrumentStoreManager } from '../components/root/start-instrument-store-manager.tsx';
import { Toaster } from '../components/toaster.tsx';
import { StartSupabaseProvider, useSupabase } from '../integrations/supabase/provider.tsx';
import { type AppShellData, getAppShellData } from '../server/app/get-app-shell-data.ts';
import { getRouteAuthSession } from '../server/auth/get-route-auth-session.ts';
import { captureClientRootError, initBrowserSentry } from '../utils/sentry.ts';
import { StartClientHintCheck, useIsMobile } from '../utils/start-client-hints.tsx';
import { useTheme } from '../utils/theme-utils.ts';

export const Route = createRootRouteWithContext<RouterContext>()({
	beforeLoad: async () => ({
		authSession: await getRouteAuthSession(),
	}),
	component: RootComponent,
	errorComponent: RootErrorComponent,
	head: () => ({
		meta: [
			{ charSet: 'utf-8' },
			{ content: 'width=device-width,initial-scale=1', name: 'viewport' },
			{ title: 'Eqity App' },
		],
	}),
	loader: async ({ context }) => {
		const shellData = await getAppShellData();

		context.queryClient.setQueryData(APP_SHELL_QUERY_KEY, shellData);
		context.queryClient.setQueryData(NAVBAR_NOTIFICATIONS_QUERY_KEY, shellData.notifications ?? []);
		context.queryClient.setQueryData(NOTIFICATIONS_PAGE_QUERY_KEY, {
			notifications: shellData.notifications ?? [],
			userLocale: shellData.requestInfo.hints.locale,
		});

		return shellData;
	},
});

type RouterDevtoolsComponent = typeof import('@tanstack/react-router-devtools').TanStackRouterDevtools;

function RootComponent() {
	return (
		<RootAppWithProviders>
			<AppDocument />
		</RootAppWithProviders>
	);
}

function RootAppWithProviders({ children }: Readonly<{ children: ReactNode }>) {
	const router = useRouter();
	const { data: shellData } = useSuspenseQuery(getAppShellDataQueryOptions());

	useEffect(() => {
		initBrowserSentry({
			dsn: shellData.env.SENTRY_DSN,
			environment: shellData.env.MODE,
			router,
		});
		ResolveIsHydrated();
		void hydrateLocalModeInstrumentCache();
	}, [router, shellData.env.MODE, shellData.env.SENTRY_DSN]);

	return (
		<IntlProvider
			defaultLocale="en"
			locale={shellData.requestInfo.hints.locale}
			messages={getMessages(shellData.requestInfo.hints.locale)}
		>
			<StartSupabaseProvider authSession={shellData.authSession}>
				<StartInstrumentStoreManager>{children}</StartInstrumentStoreManager>
			</StartSupabaseProvider>
		</IntlProvider>
	);
}

function AppDocument() {
	const { data: shellData } = useSuspenseQuery(getAppShellDataQueryOptions());
	const { accessToken } = useSupabase();
	const isAuthenticated = Boolean(accessToken ?? shellData.authSession?.accessToken);
	const isMobile = useIsMobile();
	const pathname = useRouterState({
		select: (state) => state.location.pathname,
	});
	const theme = useTheme();
	const shouldRenderAppShell = isAuthenticated && isAuthenticatedAppPath(pathname);
	const showRouterDevtools =
		shellData.env.SERVER_URL.includes('eqity-staging.fly.dev') &&
		typeof window !== 'undefined' &&
		new URLSearchParams(window.location.search).get('router-devtools') === '1';

	return (
		<RootDocument env={shellData.env} locale={shellData.requestInfo.hints.locale} theme={theme}>
			<Toaster position="bottom-right" />
			{shouldRenderAppShell ? (
				<div className={cn('flex h-dvh w-dvw overflow-hidden')}>
					<MainNavbar isMobile={isMobile} shellData={shellData} />
					<main
						className={cn(
							'fixed right-0 bg-background',
							isMobile
								? 'top-sidebar-height left-0 h-body-height w-dvw'
								: 'top-sidebar-height left-sidebar-width h-body-height w-body-width',
						)}
					>
						<Outlet />
					</main>
				</div>
			) : (
				<main className="h-dvh w-dvw overflow-auto bg-background">
					<Outlet />
				</main>
			)}
			<RouterDevtoolsSlot enabled={showRouterDevtools} />
		</RootDocument>
	);
}

function RouterDevtoolsSlot({ enabled }: Readonly<{ enabled: boolean }>) {
	const [RouterDevtools, setRouterDevtools] = useState<RouterDevtoolsComponent | null>(null);

	useEffect(() => {
		if (!enabled) {
			setRouterDevtools(null);
			return;
		}

		let isActive = true;

		void import('@tanstack/react-router-devtools')
			.then((module) => {
				if (!isActive) {
					return;
				}

				setRouterDevtools(() => module.TanStackRouterDevtools);
			})
			.catch((error) => {
				console.error('[Root] Failed to load TanStack Router devtools', error);
			});

		return () => {
			isActive = false;
		};
	}, [enabled]);

	if (!enabled || !RouterDevtools) {
		return null;
	}

	return <RouterDevtools position="bottom-right" />;
}

function getOrigin(url: string): string | null {
	try {
		return new URL(url).origin;
	} catch {
		return null;
	}
}

function getPreconnectOrigins(env: AppShellData['env']): string[] {
	const appOrigin = getOrigin(env.SERVER_URL);
	const origins = new Set<string>();

	for (const url of [env.API_URL, env.SUPABASE_URL]) {
		const origin = getOrigin(url);

		if (origin && origin !== appOrigin) {
			origins.add(origin);
		}
	}

	return Array.from(origins);
}

function RootDocument({
	children,
	env,
	locale,
	theme,
}: Readonly<{
	children: ReactNode;
	env: AppShellData['env'];
	locale: AppShellData['requestInfo']['hints']['locale'];
	theme: AppShellData['requestInfo']['userPrefs']['theme'] | AppShellData['requestInfo']['hints']['theme'];
}>) {
	const preconnectOrigins = getPreconnectOrigins(env);

	return (
		<html className={cn('h-screen w-screen', theme)} lang={locale}>
			<head>
				<StartClientHintCheck />
				<link href="https://fonts.googleapis.com" rel="preconnect" />
				<link crossOrigin="anonymous" href="https://fonts.gstatic.com" rel="preconnect" />
				{preconnectOrigins.map((origin) => (
					<link crossOrigin="anonymous" href={origin} key={`preconnect-${origin}`} rel="preconnect" />
				))}
				{preconnectOrigins.map((origin) => (
					<link href={origin} key={`dns-prefetch-${origin}`} rel="dns-prefetch" />
				))}
				<link
					href="https://fonts.googleapis.com/css2?family=JetBrains+Mono:ital,wght@0,100..800;1,100..800&display=swap"
					rel="stylesheet"
				/>
				<link href="/site.webmanifest" rel="manifest" />
				<HeadContent />
			</head>
			<body className="h-full bg-background">
				{children}
				<script
					// biome-ignore lint/security/noDangerouslySetInnerHtml: env bootstrap
					dangerouslySetInnerHTML={{
						__html: `window.ENV = ${JSON.stringify(env)}`,
					}}
				/>
				<Scripts />
			</body>
		</html>
	);
}

function RootErrorComponent({ error }: ErrorComponentProps) {
	useEffect(() => {
		captureClientRootError(error);
	}, [error]);

	return (
		<html className="dark" lang={DEFAULT_LOCALE}>
			<head>
				<StartClientHintCheck />
				<link href="https://fonts.googleapis.com" rel="preconnect" />
				<link crossOrigin="anonymous" href="https://fonts.gstatic.com" rel="preconnect" />
				<link
					href="https://fonts.googleapis.com/css2?family=JetBrains+Mono:ital,wght@0,100..800;1,100..800&display=swap"
					rel="stylesheet"
				/>
				<HeadContent />
			</head>
			<body className="bg-background transition duration-500">
				<GeneralErrorBoundaryView error={error} />
				<Scripts />
			</body>
		</html>
	);
}
